import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface CategoryCardProps {
  image: string;
  title: string;
  tagline: string;
  productCount?: number;
  badge?: string;
  ctaText?: string;
  link: string;
  featured?: boolean;
  className?: string;
}

export function CategoryCard({
  image,
  title,
  tagline,
  productCount,
  badge,
  ctaText = "Shop Now",
  link,
  featured = false,
  className,
}: CategoryCardProps) {
  return (
    <Link
      to={link}
      className={cn(
        "group relative overflow-hidden rounded-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:ring-2 hover:ring-ring",
        "bg-background border border-border",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:scale-[1.02]",
        featured && "md:col-span-2",
        className
      )}
    >
      {/* Image Container - Consistent min-height on mobile */}
      <div className={cn(
        "relative overflow-hidden",
        "min-h-[280px] md:min-h-0", // Enforce minimum height on mobile
        featured ? "aspect-[8/3]" : "aspect-[4/3]"
      )}>
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        
        {/* Badge - positioned to avoid content overlap */}
        {badge && (
          <div className="absolute top-3 right-3 md:top-4 md:right-4 bg-primary/90 backdrop-blur-sm text-primary-foreground px-2 py-1 md:px-3 md:py-1.5 rounded-full text-xs md:text-sm font-medium z-10">
            {badge}
          </div>
        )}
      </div>

      {/* Content Overlay - with gradient background for readability */}
      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/80 via-black/50 to-transparent text-white">
        <h3 className={cn(
          "font-bold mb-1 md:mb-2 transition-colors drop-shadow-lg",
          featured ? "text-xl md:text-4xl" : "text-lg md:text-3xl"
        )}>
          {title}
        </h3>
        
        <p className={cn(
          "text-white/90 mb-2 md:mb-3 drop-shadow-md",
          featured ? "text-sm md:text-lg" : "text-xs md:text-base"
        )}>
          {tagline}
        </p>
        
        {productCount !== undefined && (
          <p className="text-white/80 text-xs md:text-sm mb-2 md:mb-4">
            {productCount} {productCount === 1 ? 'product' : 'products'}
          </p>
        )}
        
        {/* CTA */}
        <div className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all text-sm md:text-base">
          <span>{ctaText}</span>
          <ArrowRight className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}
